<template>
  <q-page>
    <div class="justify-center row">
      <div v-if="!$apollo.queries.retailOrder.loading" class="col-10 q-pa-md">
        <h2 class="q-mt-sm q-mb-sm text-center">Order {{retailOrder.orderNumber}}</h2>
        <div class="text-h4 text-center">({{retailOrder.name}})</div>
        <h3>Details</h3>
        <div class="col q-gutter-md">
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Company:</div>
            <div class="col-auto q-pl-md">
              <router-link :to="`/admin/accounts/${retailOrder.account.id}`">{{retailOrder.account.companyName}}</router-link>
            </div>
          </div>
          <div class="row">
            <m-edit-item field="verified" type="datetime" graphqlType="RetailOrder" :id="id" :updateFunction="updateVerified" />
            <xero-link type="RetailOrder" :id="id" class="q-ml-md" v-if="retailOrder.verified"/>
          </div>
          <m-edit-item field="paid" type="datetime" graphqlType="RetailOrder" :id="id" />
          <m-edit-item field="packed" type="datetime" graphqlType="RetailOrder" :id="id" />
          <m-edit-item field="shipped" type="datetime" graphqlType="RetailOrder" :id="id" />
          <m-edit-item field="delivered" type="datetime" graphqlType="RetailOrder" :id="id" />
          <div class="row">
            <div class="col-1 text-right">Ship to:</div>
            <div class="col-3 q-pl-md">
              <view-shipping-details :value="retailOrder.shipping.address"/>
            </div>
          </div>
        </div>
        <div class="q-pa-md q-gutter-sm">        
          <q-btn color="primary" icon-right="fact_check" label="Download Packing Slip" @click="generatePackingSlipPDF"/>
        </div>
        <h3 class="q-mb-none">Notes</h3>
        <m-edit-item label-type="none" autogrow field="notes" type="input" graphqlType="RetailOrder" :id="id"/>
        <h3>Order</h3>
        <q-table
          :data="retailOrder.items"
          :columns="orderColumns"
          :pagination="{rowsPerPage: 0}"
          binary-state-sort
          row-key="name" >

          <template v-slot:body-cell-img="props">
            <q-td class="text-center">
              <q-img dense :src="props.value + '.png.thumb'" style="width: 25px; height: 25px;" contain>
                <q-tooltip anchor="center right" self="center left" :offset="[10, 10]"
                           transition-show="scale"
                           transition-hide="scale"
                           content-style="background: white; border: solid 1px black">
                  <q-img dense :src="props.value" style="width: 25vw; height: 25vw;" contain/>
                </q-tooltip>
              </q-img>
            </q-td>
          </template>

          <template v-slot:body-cell-reference="props">
            <q-td :props="props">
              <router-link :to="`/admin/products/${((props.row.sku||{}).product||{}).id}`">{{props.value}}</router-link>
            </q-td>
          </template>

          <template v-slot:body-cell-discountPercent="props">
            <q-td :props="props">
              {{props.value}}
              <m-popup-edit select-on-focus
                            :value="Number(props.row.discountPercent).toFixed(2)" inputType="number" v-if="!retailOrder.paid"
                            @save="v => editRetailOrderItem(props.row.skuId, { discountPercent: Number(v) })" :buttons="false"/>
            </q-td>
          </template>

          <template v-slot:body-cell-colour="props">
            <q-td :props="props">
              <div :style="{border: '1px silver solid', backgroundColor: props.row.sku.options.colour.hex, width: '10px',height: '10px',marginRight:'5px'}" class="inline-block"></div>
              {{props.row.sku.options.colour.group + ' (' + props.row.sku.options.colour.name + ')'}}
            </q-td>
          </template>

          <template v-slot:body-cell-total="props">
            <q-td :props="props">
              {{props.value}}
              <m-popup-edit select-on-focus
                            :value="Number(props.row.totalCost).toFixed(2)" inputType="number" v-if="!retailOrder.paid"
                            @save="v => editRetailOrderItem(props.row.skuId, { totalCost: Number(v) })" :buttons="false"/>
            </q-td>
          </template>

          <template v-slot:body-cell-price="props">
            <q-td :props="props">
              <q-btn icon="update" dense flat size="xs" v-if="!retailOrder.paid && !itemCostIsSameAsSkuPrice(props)"
                     @click="updateItemPriceToSkuPrice(props)">
                <q-tooltip>Update to current sku price</q-tooltip>
              </q-btn>
              <span>
                {{props.value}}
                <m-popup-edit select-on-focus
                              :value="Number(props.row.itemCost).toFixed(2)" inputType="number" v-if="!retailOrder.paid"
                              @save="v => editRetailOrderItem(props.row.skuId, { itemCost: Number(v) })" :buttons="false"/>
              </span>
            </q-td>
          </template>

          <template v-slot:body-cell-qty="props">
            <q-td :props="props" @click="!retailOrder.paid && editItemQty(props.row)">
                {{props.value}}
            </q-td>
          </template>

          <template v-slot:body-cell-actions="props">
            <q-td key="actions" :props="props">
              <span>
                <q-btn dense flat color="red" icon="clear" size="sm" @click="deleteItem(props.row)" :disabled="retailOrder.paid"></q-btn>
                <q-tooltip v-if="retailOrder.paid">Can't edit order once it's been paid.</q-tooltip>
              </span>
            </q-td>
          </template>

          <template v-slot:bottom-row v-if="!retailOrder.paid">
            <q-tr>
              <q-td colspan="100%">
                <q-select
                  v-if="addProductsMode && !$apollo.queries.products.loading"
                  filled
                  v-model="productToAdd"
                  use-input
                  dense
                  hide-selected
                  @input="addItem"
                  input-debounce="0"
                  label="Add A Product"
                  :options="filteredProducts"
                  @filter="filterFn"
                >
                  <template v-slot:no-option>
                    <q-item>
                      <q-item-section class="text-grey">
                        No results
                      </q-item-section>
                    </q-item>
                  </template>
                  <template v-slot:option="scope">
                    <q-item
                      v-bind="scope.itemProps"
                      v-on="scope.itemEvents" >
                      <q-item-section side>
                        <q-img :src="scope.opt.imageUrl" style="width: 40px; height: 40px;" contain/>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{scope.opt.label}}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
                <q-btn class="full-width" flat v-if="!addProductsMode" @click="addProductsMode = true">Add Products</q-btn>
                <div class="full-width text-center" v-if="addProductsMode && $apollo.queries.products.loading" >Loading...</div>
              </q-td>
            </q-tr>
          </template>
        </q-table>

        <div class="row full-width justify-end q-pt-md" v-if="!retailOrder.paid">
          <q-btn class="col-auto" flat color="primary" @click="discountWholeOrder">Apply A Discount To Whole Order</q-btn>
        </div>
        <div class="text-h5 q-pt-md text-right">
          Items: {{ formatCurrency(retailOrder.totalCostOfItems) }}
        </div>
        <div class="text-h5 q-pt-md text-right" v-if="retailOrder.voucherDiscountAmount">
          Voucher Discount: <span color="primary" style="color: darkred;">{{ formatCurrency(retailOrder.voucherDiscountAmount) }}</span>
        </div>
        <div class="text-h5 q-pt-md text-right">
          Shipping: {{ formatCurrency(retailOrder.shipping.cost) }}
        </div>
        <div class="text-h4 q-pt-md text-right">
          Total Cost: {{ formatCurrency(retailOrder.totalCost) }}
        </div>
        <div class="text-h5 text-right text-grey">
          Includes GST: {{ formatCurrency(retailOrder.totalCost*(1-1/1.1)) }}
        </div>

        <h3 class="q-mt-none q-mb-md">Break Downs</h3>
        <div class="row">
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Brand"
              :items="retailOrder.items"
            />
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Type"
              :items="retailOrder.items"
            />
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Material"
              :items="retailOrder.items"
            />
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Colour"
              :items="retailOrder.items"
              :group-field-callback="row => (((row.sku||{}).options||{}).colour||{}).group">
              <template v-slot:group="props">
                <div :style="{border: '1px solid silver',backgroundColor: (((props.row.item.sku||{}).options||{}).colour||{}).hex, width: '10px',height: '10px'}" class="inline-block">
                </div>
                {{props.row.group}}
              </template>
            </summary-table>
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
          <summary-table
            title="Screws You'll Need"
            sumName="Qty"
            groupName="Length"
            :items="retailOrder.items"
            :group-field-callback="row => getProductAttributeFromRow('screws', row).map(x => x.type)"
            :sum-callback="row => getProductAttributeFromRow('screws', row).map(x => x.qty * row.qty)"
            :hide-price-column="true">
          </summary-table>
          </div>          
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
          <summary-table
            title="Bolts You'll Need"
            sumName="Qty"
            groupName="Length"
            :items="retailOrder.items"
            :group-field-callback="row => getProductAttributeFromRow('bolts', row).map(x => x.type)"
            :sum-callback="row => getProductAttributeFromRow('bolts', row).map(x => x.qty * row.qty)"
            :hide-price-column="true">
          </summary-table>
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<style>
</style>

<script>
import gql from 'graphql-tag'
import MEditItem from '../../components/ui/admin/MEditItem'
import { colourToString, formatCurrency } from '../../util/string'
import SummaryTable from '../../components/summaryTable'
import adminNotifyError from '../../util/adminNotifyError'
import MPopupEdit from '../../components/ui/MPopupEdit'
import XeroLink from '../../components/XeroLink'
import ViewShippingDetails from '../../components/ui/ViewShippingDetails'
import ShippingDetails from '../../components/ui/ShippingDetails'
import getNestedFieldInObject from '@/util/getNestedFieldInObject'
import { jsPDF } from "jspdf";
import _ from "lodash";


const retailOrderFragment = gql`
  fragment RetailOrderFragment on RetailOrder {
    id
    orderNumber
    name
    notes
    createdAt
    totalCost
    totalCostOfItems
    voucherDiscountAmount
    voucherId
    shipping {
      cost
      address {
        contactName
        contactLastname
        address
        address2
        city
        state
        postCode
        country
        phoneNumber
      }
    }
    account {
      id
      companyName
    }
    placed
    paid
    verified
    packed
    shipped
    delivered
    items {
      id
      skuId
      sku {
        id
        options
        retailPrice
        stock
        product {
          id
          name
          reference
          attributes
          images {
            url
          }
          urlSlug
        }
      }
      qty
      notes
      itemCost
      discountPercent
      totalCost
    }
  }`

export default {
  name: 'AdminRetailOrder',
  components: { ShippingDetails, XeroLink, MPopupEdit, SummaryTable, MEditItem, ViewShippingDetails },
  apollo: {
    retailOrder () {
      return {
        query: gql`
        query retailOrder ($id: ID!) {
          retailOrder (id: $id) {
            ...RetailOrderFragment
          }
        }
        ${retailOrderFragment}
      `,
        variables: {
          id: this.id
        },
        fetchPolicy: 'network-only'
      }
    },
    products () {
      return {
        query: gql`
          query products ($filter: ProductFilterInput) {
            products(filter: $filter, store: Retail) {
              products {
                id,
                name,
                attributes,
                images {
                  url
                  name
                },
                urlSlug,
                description,
                skus {
                  id,
                  showInContainer,
                  retailPrice,
                  options,
                  stock
                }
              }
            }
          }
        `,
        variables () {
          return {
            filter: {}
          }
        },
        update (result) {
          return result.products?.products.flatMap(product => {
            return product.skus.map(sku => {
              if (sku.stock) {
                return Object.assign({
                  skuId: sku.id,
                  colour: sku.options?.colour,
                  qty: sku.stock,
                  imageUrl: product.images?.[0]?.url,
                  // label: `(${sku.stock}) ${product.attributes?.brand} - ${product.name} - ${sku.options?.colour?.group} (${sku.options?.colour?.name})`
                  label: "(" + sku.stock + ") " + product.attributes?.brand + " - " + product.name + " - " +  sku.options?.colour?.group + " (" + sku.options?.colour?.name + ")"
                }, product)
              } else {
                return null
              }
            }).filter(x => x !== null)
          })
        },
        skip: () => !this.addProductsMode
      }
    }
  },
  computed: {
    id () {
      return this.$route.params.id
    }
  },
  methods: {
    getNestedFieldInObject: getNestedFieldInObject,
    getProductAttributeFromRow (attributeName, row) {
      return this.getNestedFieldInObject(`sku.product.attributes.${attributeName}`, row) || []
    },
    formatCurrency: formatCurrency,
    asyncDialog (config) {
      return new Promise((resolve, reject) => {
        this.$q.dialog(config).onOk(resolve).onCancel(reject)
      })
    },
    updateItemPriceToSkuPrice (props) {
      if (props.row?.sku?.retailPrice == null) return
      this.editRetailOrderItem(props.row.skuId, { itemCost: Number(props.row?.sku?.retailPrice) })
    },
    itemCostIsSameAsSkuPrice (props) {
      return props.row?.sku?.retailPrice === props.row.itemCost
    },
    async generatePackingSlipPDF(){
      const doc = new jsPDF();
      let tableData = [];
      let totalHolds = 0;
      let totalItems = 0;
      this.retailOrder.items.forEach(item => {
        let brand, range, name, colour, holds, qty,reference;
        totalItems += Number(item.qty);
        totalHolds += Number(item.sku.product.attributes.holds) * Number(item.qty);
        qty = ""+item.qty;
        brand = item.sku.product.attributes.brand || " ";
        range = item.sku.product.attributes.range || " ";
        holds = item.sku.product.attributes.holds || " ";
        name = item.sku.product.name || " ";
        reference = item.sku.product.reference || " ";
        colour = `${item.sku.options.colour.group || " "}\n${item.sku.options.colour.name || " "}`;
        tableData.push({Brand:brand,Reference:reference,Range:range,Name:name,Colour:colour,Holds:holds,Qty:qty,Check:"  "})
      });
      
      //Logo
      doc.addImage("/img/logo.png", "PNG", 10, 10, 20, 20);

      //TITLE
      doc.setFontSize(14);
      doc.text("PACKING SLIP", 35, 30);
      doc.setFontSize(12);
      doc.text(`Order: ${this.retailOrder.orderNumber}`,40,35)

      let capCompany = this.retailOrder.account.companyName ? _.startCase(_.toLower(this.retailOrder.account.companyName)) : "";
      let capName = _.startCase(_.toLower(this.retailOrder.shipping.address.contactName));
      let capLastname = this.retailOrder.shipping.address.contactLastname ? _.startCase(_.toLower(this.retailOrder.shipping.address.contactLastname)) : "";

      //ORDER CUADRANT
      doc.setFontSize(12);
      doc.text(`Total Items (sets): ${totalItems}`, 10, 50);
      doc.text(`Total Holds: ${totalHolds}`, 10, 55);
      doc.text("Packed by:____________", 10, 65);

      //CUSTOMER CUADRANT
      let initialYPosition = 20;
      doc.setFontSize(12);
      doc.text("Ship to:", 110, initialYPosition);
      if(this.retailOrder.account.companyName) doc.text(`${capCompany || ""}`,113,initialYPosition+=5)
      doc.text(`${capName} ${capLastname || ""} `,113,initialYPosition+=5)
      if(this.retailOrder.shipping.address.phoneNumber) doc.text(`ph: ${this.retailOrder.shipping.address.phoneNumber||""}`,113,initialYPosition+=5)
      doc.text(`${this.retailOrder.shipping.address.address}`,113,initialYPosition+=5)
      if(this.retailOrder.shipping.address.address2) doc.text(`${this.retailOrder.shipping.address.address2||""}`,113,initialYPosition+=5)
      doc.text(`${this.retailOrder.shipping.address.city},${this.retailOrder.shipping.address.state},${this.retailOrder.shipping.address.postCode}`,113,initialYPosition+=5)
      doc.text(`${this.retailOrder.shipping.address.country}`,113,initialYPosition+=5)
      
      //ITEMS (TABLE)        
      let headers = [
        {
          align: "center",
          id: "Reference",
          name: "Reference",
          padding: 0,
          prompt: "Reference",
          width: 40,
        },        
        {
          align: "center",
          id: "Brand",
          name: "Brand",
          padding: 0,
          prompt: "Brand",
          width: 30,
        },
        {
          align: "center",
          id: "Range",
          name: "Range",
          padding: 0,
          prompt: "Range",
          width: 35,
        },
        {
          align: "center",
          id: "Name",
          name: "Name",
          padding: 0,
          prompt: "Name",
          width: 50,
        },                                  
        {
          align: "center",
          id: "Holds",
          name: "Holds",
          padding: 0,
          prompt: "Holds",
          width: 23,
        },
        {
          align: "center",
          id: "Colour",
          name: "Colour",
          padding: 0,
          prompt: "Colour",
          width: 32,
        },              
        {
          align: "center",
          id: "Qty",
          name: "Qty",
          padding: 0,
          prompt: "Qty",
          width: 20,
        },
        {
          align: "center",
          id: "Check",
          name: "Check",
          padding: 0,
          prompt: "",
          width: 23,
        },                             
      ]
      doc.table(10, 80, tableData, headers, { fontSize: 9, autoSize: false, margins:{ left: 0, top: 15, bottom: 10, right: 0 } });  
      doc.save(`${this.retailOrder.orderNumber}_Packing_Slip.pdf`);
      return
    },
    async editRetailOrderItem (skuId, changes) {
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation editItemInRetailOrder($orderId: ID!, $skuId: ID!, $input: RetailOrderItemInput!) {
              editItemInRetailOrder(id: $orderId, skuId: $skuId, input: $input) {
                ...RetailOrderFragment
              }
            }
            ${retailOrderFragment}
          `,
          variables: {
            orderId: this.id,
            skuId,
            input: changes
          }
        })
      } catch (error) {
        adminNotifyError(this.$q, 'Failed to update item in order.', error)
      }
    },
    async editItemQty (row) {
      const result = await this.asyncDialog({
        html: true,
        style: 'width: 50%',
        title: `Update ${row.sku?.options?.colour?.group} (${row.sku?.options?.colour?.name}) ${row.sku?.product?.name} Qty`,
        message: `<p>You're about to update the quantity for <b>${row.sku?.options?.colour?.group} (${row.sku?.options?.colour?.name}) ${row.sku?.product?.name}</b>.</p>
<p style="text-align: center"><img src="${row.sku.product?.images?.[0]?.url}" style="width: 100px; height: 100px; text-align: center" /></p>
<p>You currently have <b>${row.qty}</b> in your order and there ${row.sku?.stock > 1 ? 'are' : 'is'} <b>${row.sku?.stock}</b> remaining in stock.</p>
Enter the new quantity for this order:`,
        persistent: true,
        cancel: true,
        prompt: {
          type: 'number',
          model: row.qty
        }
      })

      let newQty = Number(result)
      if (newQty === row.qty) return
      let difference = newQty - row.qty
      const newStockLevel = row.sku.stock - difference

      if (newQty < 0) {
        await this.asyncDialog({
          html: true,
          style: 'width: 50%',
          title: `That don't make sense!`,
          message: `I may be a computer but even I get that you can't have negative qty on an invoice.  Enter a value that's more than zero.`,
          persistent: true,
          ok: `I'll try harder next time`
        })
        return
      }

      if (newStockLevel < 0) {
        await this.asyncDialog({
          html: true,
          style: 'width: 50%',
          title: `Not Enough Stock!`,
          message: `<p>Can't update the quantity of <b>${row.sku.options.colour.group} (${row.sku.options.colour.name}) ${row.sku.product.name}</b> from <b>${row.qty}</b> to <b>${newQty}</b>.</p>
<p>You tried to add <b>${Math.abs(difference)}</b> to your order but there ${row.sku.stock === 1 ? 'is' : 'are'} only <b>${row.sku.stock}</b> in stock!</p>
<p>You can edit the quantity on hand in the stock page if required.</p>`,
          persistent: true,
          cancel: true,
          ok: 'Take Me To The Stock Page'
        })
        this.$router.push(`/admin/products/${row.sku.product.id}`)
        return
      }
      await this.asyncDialog({
        html: true,
        style: 'width: 50%',
        title: `Update Stock for ${row.sku.options.colour.group} (${row.sku.options.colour.name}) ${row.sku.product.name}`,
        message: `<p>You're about to change the quantity of <b>${row.sku.product.name} in ${row.sku.options.colour.name}</b> from <b>${row.qty}</b> to <b>${newQty}</b>.</p>
<p>This will <b>${difference < 0 ? 'reduce' : 'increase'} the number in this order</b> by <b>${Math.abs(difference)}</b> and <b>${difference > 0 ? 'subtract' : 'add'} ${Math.abs(difference)}</b> ${difference > 0 ? 'from' : 'back into'} stock.</p>
<p>After this there will be <b>${newStockLevel}</b> in stock (there is currently ${row.sku.stock}).</p>`,
        persistent: true,
        cancel: true,
        ok: 'Make the change'
      })
      try {
        if (newQty === 0) {
          await this.$apollo.mutate({
            mutation: gql`mutation deleteItemFromRetailOrder($orderId: ID!, $skuId: ID!) {
                deleteItemFromRetailOrder(id: $orderId, skuId: $skuId) {
                  ...RetailOrderFragment
                }
              }
              ${retailOrderFragment}
            `,
            variables: {
              orderId: this.id,
              skuId: row.sku.id
            }
          })
        } else if (newQty > 0) {
          await this.$apollo.mutate({
            mutation: gql`mutation editItemInRetailOrder($orderId: ID!, $skuId: ID!, $input: RetailOrderItemInput!) {
                editItemInRetailOrder(id: $orderId, skuId: $skuId, input: $input) {
                  ...RetailOrderFragment
                }
              }
              ${retailOrderFragment}
            `,
            variables: {
              orderId: this.id,
              skuId: row.sku.id,
              input: {
                qty: newQty
              }
            }
          })
        }
      } catch (error) {
        adminNotifyError(this.$q, 'Error updating!', error)
      }
    },
    async updateVerified (newVal, update) {
      if (newVal !== null) {
        await this.asyncDialog({
          title: `Verify Order ${this.retailOrder.orderNumber}`,
          message: `Are you sure you want to verify this order?  This will:<br/>
- Send the user an email<br/>
- Create an invoice in Xero`,
          html: true,
          cancel: true
        })
      } else {
        await this.asyncDialog({
          title: `Un-Verify Order ${this.retailOrder.orderNumber}`,
          message: `Are you sure you want to unverify this order? The previous Xero order will still exist.  You'll have to clean that up manually.`,
          html: true,
          cancel: true
        })
      }
      update(newVal)
    },
    async deleteItem (row) {
      // TODO: Fix the delete call, use delete by item id instead of sku id!!
      const newQty = 0
      let difference = newQty - row.qty
      const newStockLevel = row.sku?.stock - difference
      await this.asyncDialog({
        html: true,
        style: 'width: 50%',
        title: `Update Stock for ${row.sku?.options?.colour?.group} (${row.sku?.options?.colour?.name}) ${row.sku?.product?.name}`,
        message: `<p>You're about to change the quantity of <b>${row.sku?.product?.name} in ${row.sku?.options?.colour?.name}</b> from <b>${row.qty}</b> to <b>${newQty}</b>.</p>
<p>This will <b>${difference < 0 ? 'reduce' : 'increase'} the number in this order</b> by <b>${Math.abs(difference)}</b> and <b>${difference > 0 ? 'subtract' : 'add'} ${Math.abs(difference)}</b> ${difference > 0 ? 'from' : 'back into'} stock.</p>
<p>After this there will be <b>${newStockLevel}</b> in stock (there is currently ${row.sku.stock}).</p>`,
        persistent: true,
        cancel: true,
        ok: 'Make the change'
      })
      try {
        if (newQty === 0) {
          await this.$apollo.mutate({
            mutation: gql`mutation deleteItemFromRetailOrder($orderId: ID!, $skuId: ID!) {
                deleteItemFromRetailOrder(id: $orderId, skuId: $skuId) {
                  ...RetailOrderFragment
                }
              }
              ${retailOrderFragment}
            `,
            variables: {
              orderId: this.id,
              skuId: row.sku.id
            }
          })
        }
      } catch (error) {
        adminNotifyError(this.$q, 'Error updating!', error)
      }
    },
    async addItem (item) {
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation addItemToRetailOrder($orderId: ID!, $input: RetailOrderItemInput!) {
              addItemToRetailOrder(id: $orderId, input: $input) {
                  ...RetailOrderFragment
              }
            }
            ${retailOrderFragment}
          `,
          variables: {
            orderId: this.id,
            input: {
              skuId: item.skuId,
              qty: 1
            }
          }
        })
      } catch (error) {
        adminNotifyError(this.$q, 'Error Adding Item!', error)
      }
      this.productToAdd = null
    },
    filterFn (val, update) {
      if (val === '') {
        update(() => {
          this.filteredProducts = this.products
        })
        return
      }

      update(() => {
        const needle = val.toLowerCase()
        this.filteredProducts = this.products.filter(v => v.label.toLowerCase().indexOf(needle) > -1)
      })
    },
    discountWholeOrder () {
      this.$q.dialog({
        title: 'Apply Discount',
        message: `What % discount do you want to apply to all items in the order?`,
        on: 'Apply',
        cancel: true,
        prompt: {
          model: '',
          type: 'number'
        }
      }).onOk(async (discountPercent) => {
        try {
          await this.$apollo.mutate({
            mutation: gql`mutation setDiscountOnAllRetailOrderItems($orderId: ID!, $discountPercent: Float!) {
                setDiscountOnAllRetailOrderItems(id: $orderId, discountPercent: $discountPercent) {
                    ...RetailOrderFragment
                }
              }
              ${retailOrderFragment}
            `,
            variables: {
              orderId: this.id,
              discountPercent: Number(discountPercent)
            }
          })
        } catch (error) {
          adminNotifyError(this.$q, 'Failed to set discount.', error)
        }
      })
    }
  },
  data () {
    return {
      retailOrder: {},
      addProductsMode: false,
      orderColumns: [
        { name: 'img', field: row => row.sku?.product?.images?.[0]?.url },
        { name: 'reference', label: 'Reference', field: row => row.sku?.product?.reference, sortable: true, align: 'left' },
        { name: 'brand', label: 'Brand', field: row => row.sku?.product?.attributes?.brand, sortable: true, align: 'left' },
        { name: 'range', label: 'Range', field: row => row.sku?.product?.attributes?.range, sortable: true, align: 'left' },
        { name: 'name', label: 'Name', field: row => row.sku?.product?.name || 'Not Found', sortable: true, align: 'left' },
        // { name: 'size', label: 'Size', field: row => row.sku?.product?.attributes?.size, sortable: true },
        // { name: 'grip', label: 'Grip', field: row => row.sku?.product?.attributes?.grip, sortable: true },
        // { name: 'colour', label: 'Colour', field: row => row.sku?.options?.colour, sortable: true, align: 'left', format: colourToString },
        { name: 'colour', label: 'Colour', field: row => colourToString(row.sku?.options?.colour), sortable: true, align: 'left' },
        { name: 'holds', label: 'Holds', field: row => Number(row.sku?.product?.attributes?.holds), sortable: true, align: 'right' },
        { name: 'price', label: 'Price', field: row => Number(row.itemCost), sortable: true, format: formatCurrency },
        { name: 'qty', label: 'Qty', field: row => Number(row.qty), sortable: true },
        // { name: 'discountPercent', label: 'Discount', field: 'discountPercent', sortable: true, format: v => `${Number(v).toFixed(1)}%` },
        { name: 'total', label: 'Total', field: row => Number(row.totalCost), sortable: true, format: formatCurrency },
        { name: 'actions', align: 'center', required: true, label: 'Actions', field: '', sortable: false }
      ],
      summaryColumns: [
        { name: 'brand', required: true, label: 'Brand', align: 'left', field: row => row.group, sortable: true },
        { name: 'holds', required: true, label: 'Holds', align: 'left', field: row => row.value, sortable: true }
      ],
      productToAdd: null,
      filteredProducts: []
    }
  }
}
</script>
